import React, { useState } from 'react';
import {
  datetime,
  dayjsToMoment,
  parseZone,
  type Moment,
} from '@common/services/datetime';
import { Select, TimePicker } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { HUMAN_TIME_FORMAT } from 'works/legacy/lib/date-helper';
import {
  Banner,
  Button,
  ButtonGroup,
  Col,
  Drawer,
  Form,
  Gutter,
  InfoTooltip,
  Row,
  Title as CommonTitle,
} from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useMutation } from '@common/hooks';
import { selectInputFilterHelper } from '@common/services';
import {
  CreateOrganizationUnitShiftConfigs,
  CreateOrganizationUnitShiftConfigsResponse,
  CreateOrganizationUnitShiftConfigsVariables,
  UpdateOrganizationUnitShiftConfig,
  UpdateOrganizationUnitShiftConfigResponse,
  UpdateOrganizationUnitShiftConfigVariables,
} from '@scheduling/graphql/mutations';
import { RoleType } from '@clinician-profile/graphql/types';
import {
  OrganizationUnitShiftConfig,
  SimplifiedWorkforceGroupType,
} from '@scheduling/graphql';
import { Colors } from '@common/tokens';
import { useCurrentMembership } from '@identity/hooks';
import { iterationMap } from 'works/legacy/services/iteration-service';

const Title = ({ title }: { title: string }) => (
  <Gutter direction="bottom" size="md">
    <CommonTitle size="h4" weight="bold">
      {title}
    </CommonTitle>
  </Gutter>
);

type ShiftsProps = {
  organizationUnitId: string;
  onClose: () => void;
  roles?: RoleType[];
  workforceGroups?: SimplifiedWorkforceGroupType[];
  handleRefetch: () => void;
  open: boolean;
  loadingData: boolean;
  shiftIngestionType?: string;
  integrationsEnabled?: string[];
  shiftTime?: OrganizationUnitShiftConfig | null;
};

export function ShiftTimesDrawer({
  organizationUnitId,
  onClose,
  roles,
  handleRefetch,
  workforceGroups,
  open,
  loadingData,
  shiftIngestionType,
  integrationsEnabled,
  shiftTime,
}: ShiftsProps) {
  const [form] = Form.useForm();
  const currentMembership = useCurrentMembership();
  const [createOrganizationUnitShiftConfigs, { loading: loadingCreate }] =
    useMutation<
      CreateOrganizationUnitShiftConfigsResponse,
      CreateOrganizationUnitShiftConfigsVariables
    >(CreateOrganizationUnitShiftConfigs);

  const [updateOrganizationUnitShiftConfig, { loading: loadingUpdate }] =
    useMutation<
      UpdateOrganizationUnitShiftConfigResponse,
      UpdateOrganizationUnitShiftConfigVariables
    >(UpdateOrganizationUnitShiftConfig);

  const [roleBannerShowed, setRoleBannerShowed] = useState<boolean>(false);

  const hasUkgTimecardsSyncEnabled =
    currentMembership.hasUkgTimecardsSyncEnabled || false;

  const closeDrawer = () => {
    form.resetFields();
    setRoleBannerShowed(false);
    onClose();
  };

  function onSuccessCreate() {
    createMessage('success', 'Shift Time successfully added.');
    handleRefetch();
    closeDrawer();
  }

  function onSuccessUpdate() {
    createMessage('success', 'Shift Time successfully updated.');
    handleRefetch();
    closeDrawer();
  }

  async function onFinishCreate(formRawData: Store) {
    try {
      const response = await createOrganizationUnitShiftConfigs({
        variables: {
          input: {
            organizationUnitId,
            roleIds: formRawData.roles.filter(
              (role: string) => role !== 'Default'
            ),
            startTime: datetime(formRawData.startTime).utc(true),
            endTime: datetime(formRawData.endTime).utc(true),
            workloadStartTime: utcDate(formRawData.workloadStartTime),
            workloadEndTime: utcDate(formRawData.workloadEndTime),
            lunchBreakMinLength: utcDate(formRawData.lunchBreakMinLength),
            lunchBreakMaxLength: utcDate(formRawData.lunchBreakMaxLength),
            lunchBreakDeductionDefault: utcDate(
              formRawData.lunchBreakDeductionDefault
            ),
            workforceGroupIds: formRawData.workforceGroups.filter(
              (wfg: string) => wfg !== 'All'
            ),
          },
        },
      });

      if (response?.errors?.length) {
        const messages = response?.errors?.map(({ message }) => message);
        createMessage('error', messages?.join(', '));
      } else {
        onSuccessCreate();
      }
    } catch {
      createMessage(
        'error',
        "Shift Time couldn't be added at this time. Try again later or contact support."
      );
    }
  }

  async function onFinishUpdate(formRawData: Store) {
    try {
      const response = await updateOrganizationUnitShiftConfig({
        variables: {
          id: shiftTime?.id ?? '',
          input: {
            organizationUnitId,
            roleIds: formRawData.roles.filter(
              (role: string) => role !== 'Default'
            ),
            startTime: datetime(formRawData.startTime).utc(true),
            endTime: datetime(formRawData.endTime).utc(true),
            workloadStartTime: utcDate(formRawData.workloadStartTime),
            workloadEndTime: utcDate(formRawData.workloadEndTime),
            lunchBreakMinLength: utcDate(formRawData.lunchBreakMinLength),
            lunchBreakMaxLength: utcDate(formRawData.lunchBreakMaxLength),
            lunchBreakDeductionDefault: utcDate(
              formRawData.lunchBreakDeductionDefault
            ),
            workforceGroupIds: formRawData.workforceGroups.filter(
              (wfg: string) => wfg !== 'All'
            ),
          },
        },
      });

      if (response?.errors?.length) {
        const messages = response?.errors?.map(({ message }) => message);
        createMessage('error', messages?.join(', '));
      } else {
        onSuccessUpdate();
      }
    } catch {
      createMessage(
        'error',
        "Shift Time couldn't be updated at this time. Try again later or contact support."
      );
    }
  }

  async function validateTimeIncrements(fieldName: string, value: Moment) {
    const timeIncrement = 15;
    if (value && value.minutes() % timeIncrement !== 0) {
      return Promise.reject(
        `${fieldName} must be in ${timeIncrement} minute increments.`
      );
    }

    return Promise.resolve();
  }

  function toMinutes(time: Moment | undefined): number | undefined {
    return time ? time.hours() * 60 + time.minutes() : undefined;
  }

  async function validateLunchBreakLength(
    currentTime: Moment | undefined,
    lowerBoundCandidates: (Moment | undefined)[],
    upperBoundCandidates: (Moment | undefined)[],
    options: {
      lowerExclusive: boolean;
      upperExclusive: boolean;
      errorMessage: string;
    }
  ) {
    if (!currentTime) return;

    const current = toMinutes(currentTime)!;

    const lowerMinutes = lowerBoundCandidates
      .map(toMinutes)
      .filter((m): m is number => m !== undefined);
    const upperMinutes = upperBoundCandidates
      .map(toMinutes)
      .filter((m): m is number => m !== undefined);

    const lowerBound = lowerMinutes.length > 0 ? Math.max(...lowerMinutes) : 0;
    const upperBound =
      upperMinutes.length > 0 ? Math.min(...upperMinutes) : 120;

    const lowerViolation = options.lowerExclusive
      ? current <= lowerBound
      : current < lowerBound;
    const upperViolation = options.upperExclusive
      ? current >= upperBound
      : current > upperBound;

    if (lowerViolation || upperViolation) {
      return Promise.reject(options.errorMessage);
    }

    return Promise.resolve();
  }

  function utcDate(date: string) {
    return date ? datetime(date).utc(true) : undefined;
  }

  function calcDisabledTime() {
    const disabledHours = iterationMap(24, (h) => h).filter((h) => h > 4);

    return {
      disabledHours: () => disabledHours,
    };
  }

  // The lunch-break bound candidates and the picker-sourced field values share
  // the same Moment-typed comparison helpers (toMinutes / validateLunchBreakLength),
  // and the raw antd v4 pickers are moment-backed at runtime. Parse the literal
  // with the canonical dayjs service, then bridge back to Moment so the synthetic
  // bounds stay type- and runtime-compatible with the picker values they're
  // compared against.
  function formatTime(time: string): Moment {
    return dayjsToMoment(datetime(time, 'HH:mm'));
  }

  // `shiftTime.*` arrive from GraphQL as ISO strings (the type's `| Moment` union
  // is legacy and never a runtime Moment here). The raw antd v4 TimePickers are
  // moment-backed at runtime, so parse the string with the canonical dayjs
  // parseZone (preserving the baked-in offset) and bridge back to Moment before
  // handing it to the picker — a type cast alone would break the picker at
  // runtime. An already-hydrated Moment value is passed through unchanged.
  const hydratePickerValue = (value?: string | Moment) =>
    typeof value === 'string' ? dayjsToMoment(parseZone(value)) : value;

  const initialValues = {
    startTime: shiftTime && hydratePickerValue(shiftTime.startTime),
    endTime: shiftTime && hydratePickerValue(shiftTime.endTime),
    workloadStartTime:
      shiftTime &&
      shiftTime.workloadStartTime &&
      hydratePickerValue(shiftTime.workloadStartTime),
    workloadEndTime:
      shiftTime &&
      shiftTime.workloadEndTime &&
      hydratePickerValue(shiftTime.workloadEndTime),
    lunchBreakMinLength:
      shiftTime &&
      shiftTime.lunchBreakMinLength &&
      hydratePickerValue(shiftTime.lunchBreakMinLength),
    lunchBreakMaxLength:
      shiftTime &&
      shiftTime.lunchBreakMaxLength &&
      hydratePickerValue(shiftTime.lunchBreakMaxLength),
    lunchBreakDeductionDefault:
      shiftTime &&
      shiftTime.lunchBreakDeductionDefault &&
      hydratePickerValue(shiftTime.lunchBreakDeductionDefault),
    roles: shiftTime?.role?.id ? [shiftTime?.role?.id] : ['Default'],
    workforceGroups:
      (shiftTime?.workforceGroups?.length ?? 0) > 0
        ? shiftTime?.workforceGroups.map((wfg) => wfg.id)
        : ['All'],
  };

  const [workloadStartTimeProvided, setWorkloadStartTimeProvided] =
    useState<boolean>(false);
  const [workloadEndTimeProvided, setWorkloadEndTimeProvided] =
    useState<boolean>(false);
  const [lunchBreakMinLengthProvided, setlunchBreakMinLengthProvided] =
    useState<boolean>(false);
  const [lunchBreakMaxLengthProvided, setlunchBreakMaxLengthProvided] =
    useState<boolean>(false);

  function validateFilledFields(): void {
    const fieldsValues = form.getFieldsValue();
    const keys = Object.keys(fieldsValues);

    const filledFields = keys.filter((key) => {
      return fieldsValues[key];
    });

    form.validateFields(filledFields);
  }

  return (
    <Drawer
      title={shiftTime ? 'Edit Shift Time' : 'Add Shift Time'}
      open={open}
      onClose={closeDrawer}
      loading={loadingData}
      footer={
        <ButtonGroup>
          <Button onClick={closeDrawer}>Cancel</Button>
          <Button
            kind="primary"
            loading={loadingUpdate || loadingCreate}
            onClick={form.submit}
          >
            Save
          </Button>
        </ButtonGroup>
      }
    >
      <Form
        className="admin-organization-unit-view__shift-times__form"
        layout="vertical"
        onFinish={shiftTime ? onFinishUpdate : onFinishCreate}
        form={form}
        initialValues={initialValues}
        preserve={false}
        onValuesChange={() => validateFilledFields()}
      >
        <Title title="Workforce Configuration" />
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Select a role',
                },
              ]}
              name="roles"
              label={
                <>
                  Role
                  <InfoTooltip
                    size="sm"
                    leftOffset="sm"
                    iconColor="elm"
                    title="Default role shift times apply to all roles unless a specific role shift time is set, which will override the default configuration."
                  />
                </>
              }
              style={{ marginBottom: 8 }}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder={
                  <span style={{ color: Colors.textDefault }}>Default</span>
                }
                onChange={(value) => {
                  if (value.length === 0) {
                    setRoleBannerShowed(false);
                    form.setFieldsValue({ roles: ['Default'] });
                  } else {
                    if (value.includes('Default') && value.length > 1) {
                      form.setFieldsValue({
                        roles: value.filter(
                          (role: string) => role !== 'Default'
                        ),
                      });
                    }
                    setRoleBannerShowed(true);
                  }
                }}
                size="large"
                style={{ width: '100%' }}
                filterOption={selectInputFilterHelper}
              >
                {roles?.map((role) => (
                  <Select.Option key={role.abbreviation} value={role.id}>
                    {role.abbreviation}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {roleBannerShowed && (
              <Banner type="info" style={{ marginTop: 16, marginBottom: 8 }}>
                This shift time will override the Default shift times for these
                roles.
              </Banner>
            )}
          </Col>
        </Row>
        <Row gutter={24} align="middle">
          <Col span={24}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Select a group',
                },
              ]}
              name="workforceGroups"
              label={
                <>
                  Groups
                  <InfoTooltip
                    size="sm"
                    leftOffset="sm"
                    iconColor="elm"
                    title="Leave this field as All to apply to all groups."
                  />
                </>
              }
              style={{ marginTop: 16 }}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder={
                  <span style={{ color: Colors.textDefault }}>All</span>
                }
                onChange={(value) => {
                  if (value.length === 0) {
                    form.setFieldsValue({ workforceGroups: ['All'] });
                  } else {
                    if (value.includes('All') && value.length > 1) {
                      form.setFieldsValue({
                        workforceGroups: value.filter(
                          (wfg: string) => wfg !== 'All'
                        ),
                      });
                    }
                  }
                }}
                size="large"
                style={{ width: '100%' }}
                filterOption={selectInputFilterHelper}
              >
                {workforceGroups?.map((wfg) => (
                  <Select.Option key={wfg.id} value={wfg.id}>
                    {wfg.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Title title="Display Time" />
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Select a start time',
                },
                {
                  validator: (_, value) =>
                    validateTimeIncrements('Start time', value),
                },
              ]}
              name="startTime"
              label="Start Time"
            >
              <TimePicker
                style={{ width: '100%' }}
                size="large"
                minuteStep={15}
                format={HUMAN_TIME_FORMAT}
                placeholder="-- : --"
                showNow={false}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: 'Select an end time',
                },
                {
                  validator: (_, value) =>
                    validateTimeIncrements('End time', value),
                },
              ]}
              name="endTime"
              label="End Time"
            >
              <TimePicker
                style={{ width: '100%' }}
                size="large"
                minuteStep={15}
                format={HUMAN_TIME_FORMAT}
                placeholder="-- : --"
                showNow={false}
              />
            </Form.Item>
          </Col>
        </Row>
        <Title title="Demand Times" />
        <Banner type="info" style={{ marginBottom: 16 }}>
          Set separate times for Works to use when calculating staffing demand
          for this shift. If left blank, Works will use the Display Times above.
        </Banner>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: workloadEndTimeProvided,
                  message:
                    'If you provide a demand end time, you must provide a demand start time.',
                },
                {
                  validator: (_, value) =>
                    validateTimeIncrements('Start Time', value),
                },
              ]}
              name="workloadStartTime"
              label="Start Time"
            >
              <TimePicker
                style={{ width: '100%' }}
                size="large"
                minuteStep={15}
                format={HUMAN_TIME_FORMAT}
                placeholder="-- : --"
                showNow={false}
                disabled={
                  !(
                    (shiftIngestionType === 'bucketing' &&
                      integrationsEnabled?.includes('ukg')) ||
                    integrationsEnabled?.includes('shift_wizard')
                  )
                }
                onChange={(value) => setWorkloadStartTimeProvided(!!value)}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: workloadStartTimeProvided,
                  message:
                    'If you provide a demand start time, you must provide a demand end time.',
                },
                {
                  validator: (_, value) =>
                    validateTimeIncrements('End Time', value),
                },
              ]}
              name="workloadEndTime"
              label="End Time"
            >
              <TimePicker
                style={{ width: '100%' }}
                size="large"
                minuteStep={15}
                format={HUMAN_TIME_FORMAT}
                placeholder="-- : --"
                showNow={false}
                disabled={
                  !(
                    (shiftIngestionType === 'bucketing' &&
                      integrationsEnabled?.includes('ukg')) ||
                    integrationsEnabled?.includes('shift_wizard')
                  )
                }
                onChange={(value) => setWorkloadEndTimeProvided(!!value)}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        {hasUkgTimecardsSyncEnabled && (
          <>
            <Title title="Shift Booking" />
            <Banner type="info" style={{ marginBottom: 16 }}>
              When booking PayCodeEdits to UKG, the staff&apos;s punch times
              will determine the lunch break length, within the set minimum and
              maximum limits. If punches are missing, the default lunch break
              duration will be used.
            </Banner>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  rules={[
                    {
                      required: lunchBreakMaxLengthProvided,
                      message:
                        'If you provide a lunch break min length, you must provide a lunch break max length.',
                    },
                    {
                      validator: (_, value) =>
                        validateLunchBreakLength(
                          value,
                          [formatTime('00:00')],
                          [
                            formatTime('02:00'),
                            form.getFieldValue('lunchBreakDeductionDefault'),
                            form.getFieldValue('lunchBreakMaxLength'),
                          ],
                          {
                            lowerExclusive: false,
                            upperExclusive: true,
                            errorMessage: `The duration of must be between "00:00" and "Lunch Break Deduction Default / 02:00".`,
                          }
                        ),
                    },
                  ]}
                  name="lunchBreakMinLength"
                  label="Lunch Break Min Length"
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    size="large"
                    format={HUMAN_TIME_FORMAT}
                    disabledTime={calcDisabledTime}
                    placeholder="-- : --"
                    showNow={false}
                    onChange={(value) =>
                      setlunchBreakMinLengthProvided(!!value)
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  rules={[
                    {
                      required: lunchBreakMinLengthProvided,
                      message:
                        'If you provide a lunch break max length, you must provide a lunch break min length.',
                    },
                    {
                      validator: (_, value) =>
                        validateLunchBreakLength(
                          value,
                          [
                            formatTime('00:00'),
                            form.getFieldValue('lunchBreakMinLength'),
                            form.getFieldValue('lunchBreakDeductionDefault'),
                          ],
                          [formatTime('02:00')],
                          {
                            lowerExclusive: true,
                            upperExclusive: false,
                            errorMessage: `The duration of must be between "Lunch Break Deduction Default" and "02:00".`,
                          }
                        ),
                    },
                  ]}
                  name="lunchBreakMaxLength"
                  label="Lunch Break Max Length"
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    size="large"
                    format={HUMAN_TIME_FORMAT}
                    disabledTime={calcDisabledTime}
                    placeholder="-- : --"
                    showNow={false}
                    onChange={(value) =>
                      setlunchBreakMaxLengthProvided(!!value)
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="lunchBreakDeductionDefault"
                  label="Lunch Break Deduction Default"
                  rules={[
                    {
                      validator: (_, value) =>
                        validateLunchBreakLength(
                          value,
                          [
                            formatTime('00:00'),
                            form.getFieldValue('lunchBreakMinLength'),
                          ],
                          [
                            form.getFieldValue('lunchBreakMaxLength') ||
                              formatTime('02:00'),
                          ],
                          {
                            lowerExclusive: false,
                            upperExclusive: false,
                            errorMessage: `The duration of must be between "00:00 / Min Break Time" and "Max Break Time /  02:00".`,
                          }
                        ),
                    },
                  ]}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    size="large"
                    format={HUMAN_TIME_FORMAT}
                    disabledTime={calcDisabledTime}
                    placeholder="-- : --"
                    showNow={false}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Drawer>
  );
}
